import { IconCheck } from '@maia/icons';
import { Button, getErrors, PageHeader, showToast } from '@maia/ui';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useMutation } from '@tanstack/react-query';

import { queryClient } from './../../../../main';
import { service } from '@maia/customer';
import { useAppForm } from './../../../../features/form/dynamic-form/dynamic-form';
import Cookies from 'js-cookie';
import { FlattenKeys } from './../../../../utils/data/flattenKeys.type';
import {
  areRequiredFieldsFilled,
  isValidDate,
  validatePhonenumber,
} from '@maia/utils';
import { format } from 'date-fns';
import { usePostalCodeLookup } from './../../../../hooks/usePostalCodeLookup';
import { useEffect } from 'react';

export const Route = createFileRoute('/_authenticated/customers/new/')({
  component: RouteComponent,
});

type FormData = {
  type: 'consumer';
  initials: string;
  firstname: string;
  lastname: string;
  email: string;
  birthdate: string;
  phone: string | undefined;
  address: {
    postalCode: string;
    number?: number;
    appendix?: string;
    street: string;
    city: string;
  };
};

function RouteComponent() {
  const navigate = useNavigate();

  const { mutateAsync: saveCustomer, isPending } = useMutation({
    mutationFn: (data: Parameters<typeof service.create>[number]) =>
      service.create({
        ...data,
        birthdate: format(new Date(data.birthdate), 'yyyy-MM-dd'),
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['customers'],
      });

      if (response.response?.token) {
        Cookies.set('auth_tokens', JSON.stringify(response.response.token), {
          secure: true,
          sameSite: 'strict',
          expires: new Date(response.response.token.refreshTokenExpiresAt),
        });
      }

      showToast({
        intent: 'success',
        title: 'Klant toegevoegd',
        config: { position: 'top-right' },
      });

      navigate({
        to: '/customers/new/$guid',
        params: { guid: response.response.contact_guid },
      });
    },
    onError: () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de klant niet toevoegen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  });

  const form = useAppForm({
    defaultValues: {
      type: 'consumer',
      address: {
        postalCode: '',
        number: undefined,
        appendix: '',
        street: '',
        city: '',
      },
    } as FormData,
    onSubmit: ({ value }) => {
      return saveCustomer({
        ...value,
        address: {
          ...value.address,
          number:
            typeof value.address.number === 'string'
              ? parseInt(value.address.number)
              : (value.address.number ?? 0),
          appendix: value.address.appendix ?? '',
          country: 'nl',
        },
      });
    },
  });

  const postalCodeLookup = usePostalCodeLookup({
    onSuccess: (address) => {
      if (address.success && address.response) {
        form.setFieldValue('address.street', address.response.street);
        form.setFieldValue('address.city', address.response.city);

        form.validateField('address.street', 'change');
        form.validateField('address.city', 'change');
        form.validateField('address', 'change');
      }
    },
    onError: (error: any) => {
      if (error?.status !== 404)
        showToast({
          intent: 'error',
          title: 'Ophalen van adres niet gelukt',
          description:
            'Er ging iets mis bij het ophalen van je adres, probeer het later opnieuw aub',
          config: { position: 'top-right' },
        });

      form.setFieldValue('address.street', '');
      form.setFieldValue('address.city', '');
    },
  });

  const requiredFields: Array<FlattenKeys<FormData>> = [
    'type',
    'initials',
    'firstname',
    'lastname',
    'email',
    'birthdate',
    'phone',
    'address',
    'address.postalCode',
    'address.number',
    'address.street',
    'address.city',
  ];

  useEffect(() => {
    return form.store.subscribe(({ prevVal, currentVal }) => {
      if (
        prevVal.values.address.postalCode !==
          currentVal.values.address.postalCode ||
        prevVal.values.address.number !== currentVal.values.address.number
      ) {
        postalCodeLookup.handleInputChange({
          postalCode: form.store.state.values.address.postalCode,
          number:
            typeof form.store.state.values.address.number === 'string'
              ? parseInt(form.store.state.values.address.number)
              : form.store.state.values.address.number,
        });
      }
    });
  }, [form.store, postalCodeLookup]);

  return (
    <div className="layout-simple">
      <PageHeader
        title="Nieuwe klant toevoegen"
        backLink={{
          to: '/customers',
        }}
        className="area-page-header"
      />

      <div className="area-page-content content-grid max-form-width">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
          className="grid grid-cols-12 gap-24 col-span-full"
        >
          <form.AppField
            name="initials"
            validators={{
              onChange: v.pipe(
                v.string('Veld is verplicht'),
                v.minLength(1, 'Veld is verplicht'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField
                  label="Voorletters"
                  className="col-span-2"
                  inputOverrides={{
                    fieldSize: 'large',
                    placeholder: 'Voorletters',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="firstname"
            validators={{
              onChange: v.pipe(
                v.string('Veld is verplicht'),
                v.minLength(1, 'Veld is verplicht'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField
                  label="Voornaam"
                  className="col-span-5"
                  inputOverrides={{
                    fieldSize: 'large',
                    placeholder: 'Voornaam',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="lastname"
            validators={{
              onChange: v.pipe(
                v.string('Veld is verplicht'),
                v.minLength(1, 'Veld is verplicht'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField
                  label="Achternaam"
                  className="col-span-5"
                  inputOverrides={{
                    fieldSize: 'large',
                    placeholder: 'Achternaam',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="email"
            validators={{
              onChange: v.pipe(
                v.string('Veld is verplicht'),
                v.email('Dit is geen valide email'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField
                  label="Email"
                  className="col-span-full"
                  inputOverrides={{
                    fieldSize: 'large',
                    placeholder: 'voorbeeld@mail.nl',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="birthdate"
            validators={{
              onChange: v.pipe(
                v.any(),
                v.custom((value) => {
                  return isValidDate(value);
                }, 'Geboortedatum is niet geldig'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.DateField
                  label="Geboortedatum"
                  className="col-span-full"
                  inputOverrides={{
                    fieldSize: 'large',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="address"
            validators={{
              onChange: v.object({
                street: v.pipe(
                  v.string('Straat is verplicht'),
                  v.minLength(1, 'Straat is verplicht'),
                ),
                number: v.pipe(
                  v.number('Huisnummer is verplicht'),
                  v.minValue(1, 'Huisnummer is verplicht'),
                ),
                appendix: v.optional(v.string()),
                city: v.pipe(
                  v.string('Stad is verplicht'),
                  v.minLength(1, 'Stad is verplicht'),
                ),
                postalCode: v.pipe(
                  v.string('Postcode is verplicht'),
                  v.minLength(6, 'Postcode moet minimaal 6 tekens bevatten'),
                ),
              }),
            }}
          >
            {(field) => {
              return (
                <field.PostcodeLookup
                  showAdditionalFields={
                    !postalCodeLookup.isIdle &&
                    !postalCodeLookup.isPending &&
                    !postalCodeLookup.isSuccess
                  }
                  showFoundAddress={
                    !postalCodeLookup.isPending &&
                    !postalCodeLookup.isError &&
                    postalCodeLookup.isSuccess
                  }
                  label="Postcode"
                  className="col-span-6"
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <form.AppField
            name="phone"
            validators={{
              onChange: v.pipe(
                v.string('Veld is verplicht'),
                v.minLength(1, 'Veld is verplicht'),
                v.custom((value) => {
                  if (typeof value !== 'string') {
                    return false;
                  }

                  return validatePhonenumber(value);
                }, 'Telefoonnummer is niet geldig'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField
                  label="Telefoonnummer"
                  className="col-span-full"
                  inputOverrides={{
                    fieldSize: 'large',
                    placeholder: '0612345678',
                  }}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>

          <div className="col-span-full flex justify-end">
            <form.Subscribe
              selector={(state) => {
                const isFilledIn = areRequiredFieldsFilled(
                  state.values,
                  requiredFields,
                );

                return [
                  state.canSubmit,
                  state.isSubmitting,
                  state.isValid,
                  isFilledIn,
                ];
              }}
            >
              {([canSubmit, isSubmitting, isValid, isFilledIn]) => (
                <Button
                  type="submit"
                  intent="cta"
                  iconLeft={IconCheck}
                  isDisabled={
                    !canSubmit ||
                    isSubmitting ||
                    isPending ||
                    !isValid ||
                    !isFilledIn
                  }
                  isLoading={isPending}
                >
                  Toevoegen
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </div>
    </div>
  );
}
